import {
  isBackendTool as isBackendToolStatic,
  computePlanHash as sharedComputePlanHash,
} from "@akem/shared";
import { LRUCache } from "lru-cache";
import { getApiEndpoint } from "../../../config/api.js";
import { newsApi } from "../../../services/perplexity.js";
import { getProfileById } from "../../../services/profile.js";
import { relationshipsApi } from "../../../services/relationships.js";
import { themesApi } from "../../../services/themes.js";
import { useToolExecutionStore } from "../hooks/useToolExecution.js";
import { useCanvasStore } from "../stores/canvasStore.js";
import type { CanvasState as StoreCanvasState, CanvasKind } from "../types/index.js";
import { agentFeedbackService } from "./agentFeedbackService.js";
import { canvasArchitect } from "./agents/canvasArchitect";
import {
  animateViewportTo,
  placePlan,
  viewportForPlan,
} from "./layout/widgetPlacer";

// Types remain the same...
export type AIProvider = "openai" | "anthropic" | "google";
export type AIModel =
  | "gpt-4-turbo"
  | "gpt-4o"
  | "claude-3-opus"
  | "claude-3-sonnet"
  | "gemini-pro";

export interface AgentConfig {
  provider?: AIProvider;
  model?: AIModel;
  temperature?: number;
  maxSteps?: number;
  stream?: boolean;
  systemPrompt?: string;
  canvasState?: CanvasState;
}

export interface ExecutionOptions {
  userId?: string;
  maxCost?: number;
  timeout?: number;
  cache?: boolean;
}

export interface ExecutionResult {
  result: any;
  steps: ExecutionStep[];
  usage: UsageStats;
  cached: boolean;
  duration: number;
}

export interface ExecutionStep {
  tool: string;
  input: any;
  output: any;
  duration: number;
  error?: string;
}

export interface UsageStats {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  estimatedCost: number;
  model: string;
}

export interface ChatMessage {
  role: "user" | "assistant" | "system" | "tool";
  content: string | null;
  toolCalls?: ToolCall[];
  tool_call_id?: string; // For tool role messages
  tool_name?: string; // For tool role messages - required by AI SDK
}

export interface ToolCall {
  id: string;
  toolName: string;
  args: any;
  result?: any;
}

export interface ToolExecutionResult {
  toolCallId: string;
  output: any;
  error?: string;
}

export interface CanvasState {
  canvasId?: string;
  widgets: CanvasWidget[];
  connections: CanvasConnection[];
  // Include discovery nodes so the backend knows what's already on the canvas
  discoveryNodes?: Array<{
    id: string;
    title: string;
    description?: string;
    prompt?: string;
    position: { x: number; y: number };
    anchorWidgetId?: string;
    pinned?: boolean;
  }>;
}

// Lightweight, delta-driven context envelope sent alongside canvasState
interface CanvasContextEnvelope {
  canvasDigest?: string;
  focus?: { activeWidgetIds?: string[]; viewport?: any };
  widgets: Array<{
    id: string;
    type: string;
    title?: string;
    configKeys?: Record<string, any>;
    lastUpdated?: number;
    dataRefs?: Array<{ kind: string; key: string }>;
    digest?: string;
    counts?: Record<string, number>;
    sample?: Array<{ id?: string; ts?: number; title?: string }>;
    ui?: Record<string, any>;
  }>;
}

export interface CanvasWidget {
  id: string;
  type: string;
  title?: string;
  position: { x: number; y: number };
  outputs: any;
}

export interface CanvasConnection {
  id: string;
  source: { widgetId: string; outputId: string };
  target: { widgetId: string; inputId: string };
}

class OrchestratorAgent {
  private cache: LRUCache<string, ExecutionResult>;
  private currentRunGroupId?: string;
  // Map of sessionId -> run groupId for fallback lookups
  private runBySession: Map<string, string> = new Map();
  // Map of run groupId -> canvasId to prevent cross-canvas bleed
  private groupCanvas: Map<string, string> = new Map();
  private pendingGroupFocus: Set<string> = new Set();

  constructor() {
    this.cache = new LRUCache<string, ExecutionResult>({
      max: 100,
      ttl: 1000 * 60 * 30, // 30 minutes
    });
  }

  // Fun, finance-themed status phrases for compact status UI
  private pickFunStatus(): string {
    const phrases = [
      "crunching numbers",
      "reading tickers",
      "scanning market heatmaps",
      "connecting the dots",
      "checking risk metrics",
      "watching volume spikes",
      "plotting price action",
      "tracing impact paths",
      "screening movers",
      "analyzing sentiment",
    ];
    const idx = Math.floor(Math.random() * phrases.length);
    return phrases[idx];
  }

  /**
   * Execute a single tool call - simplified interface for AI SDK integration
   */
  async executeToolCall(
    toolCall: ToolCall
  ): Promise<{ output?: any; error?: string }> {
    console.log("[Orchestrator] Executing single tool call:", toolCall);

    try {
      // Delegate to existing tool execution logic
      const results = await this.delegateToolCalls([toolCall]);
      const result = results[0];

      if (result.error) {
        return { error: result.error };
      }

      return { output: result.output };
    } catch (error) {
      console.error("[Orchestrator] Tool execution failed:", error);
      return {
        error: error instanceof Error ? error.message : "Tool execution failed",
      };
    }
  }

  async executeTask(
    task: string | ChatMessage[],
    config: AgentConfig = {},
    options: ExecutionOptions = {}
  ): Promise<ExecutionResult> {
    const startTime = Date.now();

    // Handle both string input (legacy) and full message history
    let messages: ChatMessage[];

    if (typeof task === "string") {
      // Legacy mode: single task string
      // Check cache if enabled
      if (options.cache !== false) {
        const cacheKey = this.generateCacheKey(task, config);
        const cached = this.cache.get(cacheKey);
        if (cached) {
          console.log("[Orchestrator] Returning cached result");
          return { ...cached, cached: true };
        }
      }

      // Initialize conversation with user's task
      messages = [
        {
          role: "user",
          content: task,
        },
      ];
    } else {
      // New mode: full conversation history
      messages = [...task]; // Copy the array to avoid mutations
    }

    const executionSteps: ExecutionStep[] = [];
    let finalResponse: string | null = null;
    let totalUsage: UsageStats = {
      promptTokens: 0,
      completionTokens: 0,
      totalTokens: 0,
      estimatedCost: 0,
      model: "openai/gpt-5", // Backend now controls the model
    };

    try {
      // Create a Run via backend to obtain canonical runId, then create local group/header
      try {
        const store = useCanvasStore.getState();
        const activeCanvasId = store.activeCanvasId;
        if (activeCanvasId) {
          // Ask backend for a new runId
          let gid: string | undefined;
          try {
            const promptText = typeof task === "string" ? task : undefined;
            const resp = await fetch(`${getApiEndpoint()}/runs`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ prompt: promptText, params: {} }),
            });
            if (resp.ok) {
              const body = await resp.json();
              gid = body.runId as string | undefined;
            }
          } catch {}
          // Fallback to local id if backend not available
          gid = gid || `run_${Math.random().toString(36).slice(2, 10)}`;
          this.currentRunGroupId = gid!;
          try {
            this.runBySession.set(gid!, gid!);
          } catch {}
          try {
            if (activeCanvasId) this.groupCanvas.set(gid!, activeCanvasId);
          } catch {}
          // Create a run header widget using smart placement to avoid overlap with existing run headers/content
          let headerPos = { x: 80, y: 120 };
          try {
            const placed = placePlan(
              { widgets: [{ type: "runHeader", position: headerPos }] } as any,
              { anchor: "belowExisting", margin: 80 }
            );
            headerPos = (placed.widgets?.[0]?.position as any) || headerPos;
          } catch {}
          const header = canvasArchitect.createWidget("runHeader", headerPos, {
            title: "Run",
          });
          if (header.success && header.widgetId) {
            store.createGroup(activeCanvasId, [header.widgetId], "Run", gid!);
            store.updateGroup(activeCanvasId, gid!, {
              status: "running",
              params: {},
            });
            // Pan/zoom the viewport to the new Run header for quick orientation
            try {
              // Defer one tick so the store state reflects the new widget before animating
              setTimeout(() => {
                try {
                  const b = viewportForPlan({
                    widgets: [{ type: "runHeader", position: headerPos }],
                  } as any);
                  if (b) animateViewportTo(b, 420);
                } catch {}
              }, 30);
            } catch {}
            // Derive initial prompt snippet for context
            let promptSnippet: string | undefined;
            try {
              if (typeof task === "string") {
                promptSnippet = task.trim();
              } else {
                const lastUser = [...messages]
                  .reverse()
                  .find((m) => m.role === "user");
                promptSnippet = (lastUser?.content || "").toString();
              }
              if (promptSnippet && promptSnippet.length > 220)
                promptSnippet = `${promptSnippet.slice(0, 220)}…`;
            } catch {}
            store.updateWidgetInputs(activeCanvasId, header.widgetId, {
              status: "running",
              prompt: promptSnippet,
              trace: [],
            });
            // Also include the prompt snippet in the group name for quick context at a glance
            try {
              if (promptSnippet && promptSnippet.length > 0) {
                store.updateGroup(activeCanvasId, gid!, {
                  name: `Run — ${promptSnippet}`,
                });
              }
            } catch {}
            // Initial trace
            this.appendRunTrace("Run started", "client:init", gid!);
            // Stay in parent canvas; do not auto-zoom into the run
            // (Users can double-click the group to zoom in when ready.)
          }
        }
      } catch {}

      // Conversational loop
      let isDone = false;
      let loopCount = 0;
      const maxLoops = config.maxSteps || 10;

      const taskDescription =
        typeof task === "string"
          ? task
          : `conversation with ${messages.length} messages`;
      console.log(
        "[Orchestrator] Starting conversational loop for task:",
        taskDescription
      );
      // Prime a visible status immediately when the loop begins
      try {
        const toolStore = useToolExecutionStore.getState();
        toolStore.setAgentThinking(`Akem is ${this.pickFunStatus()}…`);
      } catch {}

      while (!isDone && loopCount < maxLoops) {
        loopCount++;
        console.log(`[Orchestrator] Loop iteration ${loopCount}`);

        // Update compact status without implying fixed step counts
        try {
          const toolStore = useToolExecutionStore.getState();
          toolStore.setAgentThinking(`Akem is ${this.pickFunStatus()}…`);
        } catch {}

        // 1. Call backend with current message history
        const response = await this.callAgentAPI(
          messages,
          config.canvasState,
          config
        );
        const responseMessage = response.message;

        // 2. Add assistant's response to history
        messages.push(responseMessage);

        // Update usage stats
        if (response.usage) {
          totalUsage.promptTokens += response.usage.promptTokens;
          totalUsage.completionTokens += response.usage.completionTokens;
          totalUsage.totalTokens += response.usage.totalTokens;
          totalUsage.estimatedCost += response.usage.estimatedCost;
        }

        if (responseMessage.toolCalls && responseMessage.toolCalls.length > 0) {
          console.log(
            `[Orchestrator] Backend requested ${responseMessage.toolCalls.length} tool call(s)`
          );
          // Replace fun status with a concrete tool status while executing
          try {
            const toolStore = useToolExecutionStore.getState();
            const firstName = responseMessage.toolCalls[0].toolName;
            const more =
              responseMessage.toolCalls.length > 1
                ? ` (+${responseMessage.toolCalls.length - 1} more)`
                : "";
            toolStore.setAgentThinking(`Running ${firstName}${more}…`);
          } catch {}

          // Check for repeated failed tool calls (prevent infinite loops)
          const lastMessage = messages[messages.length - 2];
          if (
            lastMessage?.role === "tool" &&
            lastMessage.content?.includes('"success":false')
          ) {
            const lastToolName = lastMessage.tool_name;
            const attemptingSameTool = responseMessage.toolCalls.some(
              (tc: ToolCall) => tc.toolName === lastToolName
            );

            if (attemptingSameTool) {
              console.warn(
                `[Orchestrator] Detected repeated attempt of failed tool: ${lastToolName}`
              );
              // Mark run as needing input to resolve the block
              try {
                const store = useCanvasStore.getState();
                const activeCanvasId = store.activeCanvasId;
                if (activeCanvasId) {
                  const canvas = store.canvases.find(
                    (c) => c.id === activeCanvasId
                  );
                  if (canvas) {
                    const preferredGroupId = this.currentRunGroupId;
                    const group = preferredGroupId
                      ? (canvas.groups || []).find(
                          (g) => g.id === preferredGroupId
                        )
                      : (canvas.groups || []).slice(-1)[0];
                    if (group) {
                      store.updateGroup(activeCanvasId, group.id, {
                        status: "needs_input",
                      });
                      const header = group.widgets.find(
                        (w) => w.type === "runHeader"
                      );
                      // Provide context-aware CTA suggestions
                      const nextSteps: string[] = (() => {
                        const name = String(lastToolName || "").toLowerCase();
                        if (
                          name.includes("searchorcreateprofile") ||
                          name.includes("loadprofileworkspace")
                        ) {
                          return [
                            "Specify a ticker (e.g., NVDA)",
                            "Provide full company name",
                          ];
                        }
                        if (
                          name.includes("fetchmarketdata") ||
                          name.includes("searchsymbols")
                        ) {
                          return [
                            "Provide one or more symbols (e.g., AAPL, MSFT)",
                            "Set a date range or days",
                          ];
                        }
                        if (name.includes("calculatetechnicals")) {
                          return [
                            "Provide a symbol and indicators (e.g., RSI, MACD)",
                          ];
                        }
                        if (name.includes("connectwidgets")) {
                          return ["Confirm which outputs/inputs to connect"];
                        }
                        return ["Clarify the parameters to continue"];
                      })();
                      if (header) {
                        store.updateWidgetInputs(activeCanvasId, header.id, {
                          status: "needs_input",
                          summary: "Run paused: needs your input to proceed.",
                          nextSteps,
                        });
                      }
                      this.appendRunTrace(
                        "Run paused: needs input",
                        "guard:needs_input",
                        group.id
                      );
                    }
                  }
                }
              } catch {}
              // Add a more explicit error message
              messages.push({
                role: "tool",
                tool_call_id: responseMessage.toolCalls[0].id,
                tool_name: lastToolName,
                content: JSON.stringify({
                  error: `Tool ${lastToolName} failed in previous attempt. Please try a different approach or provide more specific parameters.`,
                  success: false,
                  repeated_failure: true,
                }),
              });
              continue; // Skip execution and let backend handle the error
            }
          }

          // 3. Execute tool calls (groupId is injected server-side)
          const normalizedCalls = responseMessage.toolCalls;
          const toolResults = await this.delegateToolCalls(normalizedCalls);

          // 4. Add tool results to history
          toolResults.forEach((result) => {
            // Find the corresponding tool call to get the tool name
            const toolCall = responseMessage.toolCalls!.find(
              (tc: ToolCall) => tc.id === result.toolCallId
            );

            // Include error in tool result for backend to see
            const toolContent = result.error
              ? JSON.stringify({ error: result.error, success: false })
              : JSON.stringify(result.output);

            messages.push({
              role: "tool",
              tool_call_id: result.toolCallId,
              tool_name: toolCall?.toolName || "unknown",
              content: toolContent,
            });

            // Track execution steps with clean output
            if (toolCall) {
              // Clean the output for the execution step
              let cleanOutput = result.output;
              if (typeof result.output === "object" && result.output.message) {
                cleanOutput = { ...result.output };
              }

              executionSteps.push({
                tool: toolCall.toolName,
                input: toolCall.args,
                output: cleanOutput,
                error: result.error,
                duration: 0, // We'll track this properly in delegateToolCalls
              });
            }
          });

          // Update canvas state after tool execution
          config.canvasState = this.getCurrentCanvasState();

          // If the agent finalized the turn via 'answer', stop the loop
          try {
            const answered = responseMessage.toolCalls.some(
              (tc: ToolCall) => tc.toolName === "answer"
            );
            if (answered) {
              // Prefer the summary in the answer payload if available
              const answerCall = responseMessage.toolCalls.find(
                (tc: ToolCall) => tc.toolName === "answer"
              );
              const summary = answerCall?.args?.summary as string | undefined;
              if (summary && summary.length > 0) {
                finalResponse = summary;
              }
              // Mark loop as done and set a friendly status
              isDone = true;
              try {
                const toolStore = useToolExecutionStore.getState();
                toolStore.setAgentThinking("Completed");
              } catch {}
              continue; // proceed to loop check
            }
          } catch {}

          // Loop continues...
        } else {
          // 5. No tool calls, agent is done
          console.log("[Orchestrator] Conversation complete");
          try {
            const toolStore = useToolExecutionStore.getState();
            toolStore.setAgentThinking("Completed");
          } catch {}
          // Clean the response content if it contains control characters
          if (responseMessage.content) {
            // Check if content looks like a raw tool response with control characters
            if (
              responseMessage.content.includes("<ctrl46>") ||
              responseMessage.content.startsWith("response:")
            ) {
              // Try to extract meaningful message from tool response
              const jsonMatch = responseMessage.content.match(/\{.*?\}/s);
              if (jsonMatch) {
                try {
                  const parsed = JSON.parse(jsonMatch[0]);
                  finalResponse =
                    parsed.message ||
                    parsed.result ||
                    "Task completed successfully";
                } catch {
                  finalResponse = "Task completed successfully";
                }
              } else {
                finalResponse = "Task completed successfully";
              }
            } else {
              finalResponse = responseMessage.content;
            }
          } else {
            // If no content but we have execution steps, summarize them
            if (executionSteps.length > 0) {
              const lastStep = executionSteps[executionSteps.length - 1];
              if (lastStep.output?.message) {
                finalResponse = lastStep.output.message;
              } else {
                finalResponse = `Completed ${executionSteps.length} action${
                  executionSteps.length > 1 ? "s" : ""
                }`;
              }
            } else {
              finalResponse = "Task completed";
            }
          }
          // Mark current run as complete and update Run Header inputs with the final response
          try {
            const store = useCanvasStore.getState();
            const activeCanvasId = store.activeCanvasId;
            if (activeCanvasId) {
              const canvas = store.canvases.find(
                (c) => c.id === activeCanvasId
              );
              if (canvas) {
                const preferredGroupId = this.currentRunGroupId;
                const group = preferredGroupId
                  ? (canvas.groups || []).find((g) => g.id === preferredGroupId)
                  : (canvas.groups || []).slice(-1)[0];
                if (group) {
                  store.updateGroup(activeCanvasId, group.id, {
                    status: "complete",
                  });
                  const header = group.widgets.find(
                    (w) => w.type === "runHeader"
                  );
                  if (header) {
                    store.updateWidgetInputs(activeCanvasId, header.id, {
                      status: "complete",
                      summary: finalResponse,
                      final: finalResponse,
                    });
                  }
                  this.appendRunTrace("Run completed", "final", group.id);
                }
              }
            }
          } catch {}
          isDone = true;
        }
      }

      if (loopCount >= maxLoops) {
        console.warn(
          `[Orchestrator] Reached maximum loop iterations (${maxLoops})`
        );
        if (!finalResponse) {
          finalResponse =
            "Task execution reached maximum iterations. Please try breaking down your request into smaller steps.";
        }
      }

      const executionResult: ExecutionResult = {
        result: finalResponse,
        steps: executionSteps,
        usage: totalUsage,
        cached: false,
        duration: Date.now() - startTime,
      };

      // Cache result if enabled
      if (options.cache !== false && typeof task === "string") {
        const cacheKey = this.generateCacheKey(task, config);
        this.cache.set(cacheKey, executionResult);
      }

      return executionResult;
    } catch (error) {
      console.error("[Orchestrator] Execution failed:", error);
      throw new Error(
        `Orchestration failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  }

  private async delegateToolCalls(
    toolCalls: ToolCall[]
  ): Promise<ToolExecutionResult[]> {
    const results: ToolExecutionResult[] = [];
    const toolStore = useToolExecutionStore.getState();

    const deriveRunGroupId = (args: any): string | undefined => {
      try {
        if (!args) return this.currentRunGroupId;
        if (args.groupId) return String(args.groupId);
        if (args.sessionId)
          return this.runBySession.get(String(args.sessionId));
        if (args.target?.groupId) return String(args.target.groupId);
        return this.currentRunGroupId;
      } catch {
        return this.currentRunGroupId;
      }
    };

    const summarizeArgs = (args: any): string => {
      try {
        if (!args || typeof args !== "object") return "";
        const keys = Object.keys(args).filter(
          (k) =>
            ![
              "sessionId",
              "groupId",
              "target",
              "__idempotentAck",
              "__planHash",
            ].includes(k)
        );
        const sample = keys
          .slice(0, 3)
          .map(
            (k) =>
              `${k}=${
                typeof args[k] === "string"
                  ? args[k]
                  : Array.isArray(args[k])
                  ? `[…]`
                  : typeof args[k]
              }`
          );
        return sample.length ? `(${sample.join(", ")})` : "";
      } catch {
        return "";
      }
    };

    // Debug log to see what we're receiving from backend
    console.log(
      "[Orchestrator] Delegating tool calls:",
      JSON.stringify(toolCalls, null, 2)
    );

    for (const toolCall of toolCalls) {
      let output: any;
      let error: string | undefined;

      // Track tool execution start
      const category = this.isBackendTool(toolCall.toolName)
        ? "backend"
        : "frontend";
      const toolId = toolStore.startToolExecution(
        toolCall.toolName,
        toolCall.args,
        category
      );

      try {
        // Scope this tool call to its intended canvas
        try {
          const targetCanvasId = this.resolveCanvasIdForTool(toolCall);
          canvasArchitect.setTargetCanvas(targetCanvasId);
          // Make available to handlers that touch the store directly
          (toolCall.args ||= {}).__targetCanvasId = targetCanvasId;
        } catch {}
        // Surface a live step before executing each tool
        try {
          if (toolCall.toolName !== "answer") {
            const g = deriveRunGroupId(toolCall.args);
            this.appendRunTrace(
              `Starting ${toolCall.toolName} ${summarizeArgs(
                toolCall.args
              )}`.trim(),
              toolCall.toolName,
              g
            );
          }
        } catch {}

        // The main architectural switch
        switch (toolCall.toolName) {
          // --- Handoff to Specialist Agent ---
          case "delegateToCanvasArchitect":
            output = await this.handleCanvasArchitectDelegation(toolCall);
            break;

          // --- Low-level tool execution (for the CanvasArchitect sub-loop) ---
          case "createWidget":
            output = this.handleCreateWidget(toolCall);
            break;
          case "connectWidgets":
            output = this.handleConnectWidgets(toolCall);
            break;
          case "updateWidget":
            output = this.handleUpdateWidget(toolCall);
            break;
          case "updateWidgetInputs":
            output = this.handleUpdateWidgetInputs(toolCall);
            break;
          case "deleteWidget":
            output = this.handleDeleteWidget(toolCall);
            break;
          case "createDiscoveryNode":
            output = this.handleCreateDiscoveryNode(toolCall);
            break;
          case "navigateToSubcanvas":
            output = this.handleNavigateToSubcanvas(toolCall);
            break;
          case "applyCanvasPlan":
            // Idempotent acknowledge from backend: do not re-apply, just return success
            if (toolCall.args && toolCall.args.__idempotentAck) {
              output = {
                success: true,
                message: "Plan already applied this turn",
                alreadyApplied: true,
                planHash:
                  toolCall.args.__planHash ||
                  sharedComputePlanHash(toolCall.args),
              };
            } else {
              output = this.handleApplyCanvasPlan(toolCall);
            }
            break;

          // --- Backend tools that signal completion ---
          case "answer":
            // The answer tool signals the end of the conversation turn
            console.log(
              "[Orchestrator] Answer tool called, ending conversation turn"
            );
            output = {
              success: true,
              message: "Conversation turn complete",
              summary: toolCall.args?.summary || "Task completed",
            };
            // Mark the current run (group) as complete and update header widget inputs
            try {
              const store = useCanvasStore.getState();
              const preferredGroupId =
                toolCall.args?.groupId ||
                (toolCall.args?.sessionId
                  ? this.runBySession.get(toolCall.args.sessionId)
                  : undefined) ||
                this.currentRunGroupId;
              let targetCanvasId: string | undefined;
              if (preferredGroupId) targetCanvasId = this.groupCanvas.get(preferredGroupId);
              targetCanvasId = targetCanvasId || (toolCall.args?.__targetCanvasId as string | undefined) || store.activeCanvasId;
              if (targetCanvasId) {
                const canvas = store.canvases.find((c) => c.id === targetCanvasId);
                if (canvas) {
                  const group = preferredGroupId
                    ? (canvas.groups || []).find(
                        (g) => g.id === preferredGroupId
                      )
                    : (canvas.groups || []).slice(-1)[0];
                  if (group) {
                    store.updateGroup(targetCanvasId, group.id, {
                      status: "complete",
                    });
                    const header = group.widgets.find(
                      (w) => w.type === "runHeader"
                    );
                    if (header) {
                      store.updateWidgetInputs(targetCanvasId, header.id, {
                        status: "complete",
                        summary: toolCall.args?.summary || "Task completed",
                        final:
                          toolCall.args?.fullText ||
                          toolCall.args?.summaryLong ||
                          toolCall.args?.finalText ||
                          toolCall.args?.summary ||
                          "Task completed",
                        finalView: toolCall.args?.finalView,
                        finalData: toolCall.args?.finalData,
                        nextSteps: Array.isArray(toolCall.args?.nextSteps)
                          ? toolCall.args?.nextSteps
                          : undefined,
                        actionsTaken: Array.isArray(toolCall.args?.actionsTaken)
                          ? toolCall.args?.actionsTaken
                          : undefined,
                        cost: toolCall.args?.cost,
                      });
                    }
                    // Trace completion
                    this.appendRunTrace("Run completed", "answer", group.id);
                  }
                }
              }
              // Clear current run after completion if we matched it
              try {
                if (
                  toolCall.args?.groupId &&
                  this.currentRunGroupId === toolCall.args.groupId
                ) {
                  this.currentRunGroupId = undefined;
                }
              } catch {}
            } catch {}
            break;

          case "plan":
          case "reflect":
            // These are backend planning/reflection tools - just acknowledge them
            console.log(
              `[Orchestrator] Backend ${toolCall.toolName} tool acknowledged`
            );
            output = { success: true };
            break;

          // --- Other direct tools (if any) ---
          default:
            console.warn(`[Orchestrator] Unknown tool: ${toolCall.toolName}`);
            output = { error: `Unknown tool: ${toolCall.toolName}` };
            error = `Unknown tool: ${toolCall.toolName}`;
        }
      } catch (e) {
        error = e instanceof Error ? e.message : "Unknown error";
        output = { error };
        console.error(
          `[Orchestrator] Tool execution error for ${toolCall.toolName}:`,
          error
        );
      } finally {
        try { canvasArchitect.setTargetCanvas(undefined); } catch {}
      }

      // Track tool completion or failure
      if (error) {
        toolStore.failToolExecution(toolId, error);
      } else {
        toolStore.completeToolExecution(toolId, output);
      }

      // Emit a post-step message to the run trace
      try {
        const g = deriveRunGroupId(toolCall.args);
        if (toolCall.toolName !== "answer") {
          const doneMsg = error
            ? `Failed ${toolCall.toolName}${error ? `: ${error}` : ""}`
            : `Completed ${toolCall.toolName}`;
          this.appendRunTrace(doneMsg, toolCall.toolName, g);
        }
      } catch {}

      results.push({
        toolCallId: toolCall.id,
        output,
        error,
      });
    }

    return results;
  }

  // Determine target canvas for a tool call
  private resolveCanvasIdForTool(toolCall: ToolCall): string | undefined {
    try {
      const store = useCanvasStore.getState();
      // Prefer explicit canvas identity from args (applyCanvasPlan or target.canvas)
      const c = (toolCall.args && (toolCall.args.canvas || toolCall.args.target?.canvas)) || undefined;
      if (c && (c.key || c.name || c.kind)) {
        return this.ensureCanvasByIdentity({
          key: typeof c.key === 'string' ? c.key : undefined,
          name: typeof c.name === 'string' ? c.name : undefined,
          kind: typeof c.kind === 'string' ? (c.kind as CanvasKind) : undefined,
        });
      }
      // Use explicit target canvasId if provided
      const explicitId = toolCall.args?.target?.canvasId || toolCall.args?.canvasId;
      if (explicitId && typeof explicitId === 'string') return explicitId;
      // Derive from run group mapping
      const gid = this.deriveRunGroupId(toolCall.args);
      if (gid && this.groupCanvas.has(gid)) return this.groupCanvas.get(gid);
      // Fallback to active canvas
      return store.activeCanvasId;
    } catch {
      return undefined;
    }
  }

  private deriveRunGroupId(args: any): string | undefined {
    try {
      if (!args) return this.currentRunGroupId;
      if (args.groupId) return String(args.groupId);
      if (args.sessionId) return this.runBySession.get(String(args.sessionId));
      if (args.target?.groupId) return String(args.target.groupId);
      return this.currentRunGroupId;
    } catch {
      return this.currentRunGroupId;
    }
  }

  // Ensure a canvas exists by identity (key preferred); returns its id without changing active canvas
  private ensureCanvasByIdentity(identity: { key?: string; kind?: CanvasKind; name?: string }): string {
    const store = useCanvasStore.getState();
    if (identity.key) {
      const found = store.canvases.find((c) => c.key === identity.key);
      if (found) return found.id;
    }
    const name = identity.name || 'Canvas';
    const kind = identity.kind || ('custom' as CanvasKind);
    const id = store.createCanvas(name, { kind, key: identity.key, makeActive: false });
    store.setCanvasIdentity(id, { key: identity.key, kind, name });
    return id;
  }

  /**
   * Handles the entire sub-loop for the CanvasArchitectAgent.
   * This is the core of the handoff mechanism.
   */
  private async handleCanvasArchitectDelegation(
    toolCall: ToolCall
  ): Promise<any> {
    console.log(
      "[Orchestrator] Delegating to CanvasArchitectAgent with instruction:",
      toolCall.args.instruction
    );

    // 1. Initialize the specialist's conversation
    let specialistMessages: ChatMessage[] = [];
    const instruction = toolCall.args.instruction;
    let isDone = false;
    let loopCount = 0;
    const maxLoops = 15; // Generous limit for complex UI builds

    while (!isDone && loopCount < maxLoops) {
      loopCount++;
      console.log(`[CanvasArchitect Sub-Loop] Iteration ${loopCount}`);

      // Track Canvas Architect progress
      const toolStore = useToolExecutionStore.getState();
      toolStore.setAgentThinking(
        `Canvas Architect: Step ${loopCount} of ${maxLoops}`
      );

      // 2. Call the specialist agent's endpoint
      const response = await this.callCanvasArchitectAPI(
        specialistMessages,
        instruction,
        this.getCurrentCanvasState()
      );
      const responseMessage = response.message;

      // 3. Add assistant's response to specialist's history
      specialistMessages.push(responseMessage);

      if (responseMessage.toolCalls && responseMessage.toolCalls.length > 0) {
        // 4. Specialist requested low-level tools. Execute them.
        console.log(
          `[CanvasArchitect Sub-Loop] Executing ${responseMessage.toolCalls.length} low-level tool(s)`
        );
        const toolResults = await this.delegateToolCalls(
          responseMessage.toolCalls
        ); // Recursive call to handle simple tools

        // 5. Add tool results to specialist's history
        toolResults.forEach((result) => {
          const originalToolCall = responseMessage.toolCalls!.find(
            (tc: ToolCall) => tc.id === result.toolCallId
          );
          specialistMessages.push({
            role: "tool",
            tool_call_id: result.toolCallId,
            tool_name: originalToolCall?.toolName || "unknown",
            content: JSON.stringify(result.output),
          });
        });
        // Loop continues...
      } else {
        // 6. No more tool calls. The specialist is done.
        console.log(
          "[CanvasArchitect Sub-Loop] Complete. Final message:",
          responseMessage.content
        );
        isDone = true;
        // The final content from the specialist is the result of the delegation.
        return { success: true, result: responseMessage.content };
      }
    }

    if (loopCount >= maxLoops) {
      console.warn("[CanvasArchitect Sub-Loop] Reached maximum iterations.");
      return { success: false, error: "Canvas architect took too many steps." };
    }
  }

  private handleNavigateToSubcanvas(toolCall: ToolCall) {
    const args = toolCall.args || {};
    const store = useCanvasStore.getState();
    const activeCanvasId = store.activeCanvasId;
    if (!activeCanvasId) {
      throw new Error("No active canvas available for navigation");
    }

    const canvas = store.canvases.find(
      (c) => c.id === activeCanvasId
    ) as StoreCanvasState | undefined;
    if (!canvas) {
      throw new Error("Active canvas not found");
    }

    const {
      domain,
      profileId,
      groupId: providedGroupId,
      canvasKey,
      canvasKind,
      name,
    } = args as Record<string, any>;

    // Narrow unknown string to CanvasKind union safely
    const asCanvasKind = (k: any): CanvasKind | undefined =>
      k === "market" || k === "profile" || k === "custom" ? (k as CanvasKind) : undefined;

    if (canvasKey || canvasKind || name) {
      store.setCanvasIdentity(activeCanvasId, {
        key: typeof canvasKey === "string" ? canvasKey : undefined,
        kind: asCanvasKind(canvasKind),
        name: typeof name === "string" ? name : undefined,
      });
    }

    const resolvedDomain =
      typeof domain === "string" && domain.length > 0 ? domain : "overview";
    const resolvedProfileId =
      typeof profileId === "string" && profileId.length > 0
        ? profileId
        : this.inferProfileIdFromCanvas(canvas);

    const baseKey = `${resolvedProfileId || canvas.key || activeCanvasId}:${resolvedDomain}`;
    const derivedGroupId =
      typeof providedGroupId === "string" && providedGroupId.length > 0
        ? providedGroupId
        : `subcanvas:${sharedComputePlanHash(baseKey)}`;

    const existingGroup = (canvas.groups || []).find(
      (g) => g.id === derivedGroupId
    );

    if (existingGroup) {
      try {
        store.zoomToGroup(activeCanvasId, derivedGroupId);
      } catch (err) {
        console.warn(
          "[Orchestrator] Failed to zoom to existing subcanvas",
          err
        );
      }
      this.pendingGroupFocus.delete(derivedGroupId);
      return {
        success: true,
        groupId: derivedGroupId,
        message: `Focused existing subcanvas for ${resolvedDomain}`,
      };
    }

    this.pendingGroupFocus.add(derivedGroupId);
    return {
      success: true,
      groupId: derivedGroupId,
      pending: true,
      message: `Awaiting plan for ${resolvedDomain} subcanvas`,
    };
  }

  // --- Low-level Tool Handlers ---

  private handleCreateWidget(toolCall: ToolCall) {
    // Prevent modifications on read-only canvases (market/profile dashboards)
    try {
      const store = useCanvasStore.getState();
      const targetCanvasId =
        (toolCall.args?.__targetCanvasId as string | undefined) || store.activeCanvasId;
      if (targetCanvasId) {
        const c = store.canvases.find((cc) => cc.id === targetCanvasId);
        if (c && c.kind && c.kind !== 'custom') {
          return { success: false, error: 'Canvas is read-only' };
        }
      }
    } catch {}
    const { type, title, config } = toolCall.args;
    let position = toolCall.args?.position as
      | { x: number; y: number }
      | undefined;
    // Auto-inject profileId for inference-heavy widgets when canvas has one
    const inferenceTypes = new Set<string>([
      "impactAnalysis",
      "impactDashboard",
      "impactExplorer",
    ]);
    let effectiveConfig = { ...(config || {}) } as any;
    try {
      if (inferenceTypes.has(String(type))) {
        const store = useCanvasStore.getState();
        const active = store.getActiveCanvas();
        const profileIds = new Set(
          (active?.widgets || [])
            .map((w) => (w.config as any)?.profileId)
            .filter((pid) => typeof pid === "string" && pid.length > 0)
        );
        // Only auto-inject when there is exactly one unambiguous profileId on the canvas
        if (profileIds.size === 1 && !effectiveConfig.profileId) {
          const [onlyId] = Array.from(profileIds);
          effectiveConfig.profileId = onlyId;
        }
      }
    } catch {}
    // Smart placement for single widget when position not specified
    try {
      if (!position) {
        const seedPlan = {
          widgets: [{ type, position: { x: 180, y: 220 } }],
        } as any;
        const placed = placePlan(seedPlan, {
          anchor: "rightOfExisting",
          margin: 100,
        });
        position = placed.widgets[0]?.position || { x: 180, y: 220 };
        const b = viewportForPlan(placed as any);
        if (b) animateViewportTo(b, 320);
      }
    } catch {}
    const result = canvasArchitect.createWidget(type, position, {
      ...effectiveConfig,
      title,
    });
    if (result.success) {
      try {
        const gid =
          toolCall.args?.groupId ??
          (toolCall.args?.sessionId
            ? this.runBySession.get(toolCall.args.sessionId)
            : undefined);
        this.appendRunTrace(
          `Created ${String(type)} widget`,
          "createWidget",
          gid
        );
      } catch {}
      // Fire-and-forget prefetch for certain widgets to warm caches
      try {
        const pid = effectiveConfig?.profileId as string | undefined;
        if (pid && typeof pid === "string") {
          setTimeout(() => {
            try {
              if (String(type) === "themeAnalysis") {
                themesApi.discover(pid).catch(() => {});
              } else if (String(type) === "newsSentiment") {
                const sym =
                  (effectiveConfig?.ticker as string | undefined) ||
                  (/^[A-Z.]{1,6}$/.test(pid) ? pid : undefined);
                if (sym)
                  newsApi.getStockNews(sym, { profileId: pid }).catch(() => {});
              } else if (String(type) === "relationshipGraph") {
                relationshipsApi.getRelationships(pid).catch(() => {});
              }
            } catch {}
          }, 0);
        }
      } catch {}
      return {
        success: true,
        widgetId: result.widgetId,
        message: `Created ${type} widget.`,
      };
    }
    throw new Error(result.error || "Failed to create widget");
  }

  private handleApplyCanvasPlan(toolCall: ToolCall) {
    const args = toolCall.args || {};
    const { plan, dsl, showPlaceholders, groupId, params, canvas } = args;
    try {
      // Respect provided canvas identity and scope operations to it
      let targetCanvasId = toolCall.args?.__targetCanvasId as string | undefined;
      if (!targetCanvasId && canvas) {
        const asCanvasKind = (k: any): CanvasKind | undefined =>
          k === "market" || k === "profile" || k === "custom" ? (k as CanvasKind) : undefined;
        targetCanvasId = this.ensureCanvasByIdentity({
          key: typeof canvas.key === 'string' ? canvas.key : undefined,
          kind: asCanvasKind(canvas.kind),
          name: typeof canvas.name === 'string' ? canvas.name : undefined,
        });
        try { canvasArchitect.setTargetCanvas(targetCanvasId); } catch {}
      }
      // Enforce read-only policy: block arbitrary plan application on Market canvas
      try {
        const store = useCanvasStore.getState();
        const cid = targetCanvasId || store.activeCanvasId;
        if (cid) {
          const c = store.canvases.find((cc) => cc.id === cid);
          if (c && c.kind === 'market') {
            return { success: false, error: 'Canvas is read-only' };
          }
        }
      } catch {}
    } catch (err) {
      console.warn("[Orchestrator] Unable to set canvas identity", err);
    }
    let layout = plan || (dsl ? this.parseCanvasDSL(String(dsl)) : undefined);
    if (
      !layout ||
      !Array.isArray(layout.widgets) ||
      layout.widgets.length === 0
    ) {
      throw new Error(
        "applyCanvasPlan requires a plan with widgets or a valid DSL"
      );
    }

    const createdWidgetIds: string[] = [];
    // Smart placement: shift planned widgets relative to existing canvas to avoid overlap and improve readability
    try {
      const placed = placePlan(layout as any, {
        anchor: "rightOfExisting",
        margin: 120,
      });
      if (
        placed &&
        Array.isArray((placed as any).widgets) &&
        (placed as any).widgets.length === (layout as any).widgets.length
      ) {
        layout = placed as any;
      }
    } catch {}
    // Try to find a profileId from any widget in the plan ahead of creation
    const planProfileId = (layout.widgets || [])
      .map((w: any) => w?.config?.profileId)
      .find((pid: any) => typeof pid === "string" && pid.length > 0);
    const inferenceTypes = new Set<string>([
      "impactAnalysis",
      "impactDashboard",
      "impactExplorer",
    ]);
    const prefetchTypes = new Set<string>([
      "themeAnalysis",
      "newsSentiment",
      "relationshipGraph",
    ]);
    // Create widgets
    layout.widgets.forEach((w: any, idx: number) => {
      const position = w.position || { x: 100 + idx * 300, y: 200 };
      const baseConfig = { ...(w.config || {}) } as any;
      if (
        planProfileId &&
        inferenceTypes.has(String(w.type)) &&
        !baseConfig.profileId
      ) {
        baseConfig.profileId = planProfileId;
      }
      const result = canvasArchitect.createWidget(w.type, position, {
        ...baseConfig,
        title: w.title,
      });
      if (!result.success || !result.widgetId)
        throw new Error(
          result.error || `Failed to create widget at index ${idx}`
        );
      createdWidgetIds.push(result.widgetId);

      // Prefetch for certain widgets when we have a plan profileId
      try {
        if (planProfileId && prefetchTypes.has(String(w.type))) {
          setTimeout(() => {
            try {
              if (String(w.type) === "themeAnalysis") {
                themesApi.discover(planProfileId).catch(() => {});
              } else if (String(w.type) === "newsSentiment") {
                const sym =
                  (baseConfig?.ticker as string | undefined) ||
                  (/^[A-Z.]{1,6}$/.test(planProfileId)
                    ? planProfileId
                    : undefined);
                if (sym)
                  newsApi
                    .getStockNews(sym, { profileId: planProfileId })
                    .catch(() => {});
              } else if (String(w.type) === "relationshipGraph") {
                relationshipsApi
                  .getRelationships(planProfileId)
                  .catch(() => {});
              }
            } catch {}
          }, 0);
        }
      } catch {}
    });

    // If a profileId is present in the plan, hydrate the Profile Header widget's input with the profile object
    try {
      if (planProfileId) {
        const store = useCanvasStore.getState();
        const activeCanvasId = store.activeCanvasId;
        const canvas = activeCanvasId
          ? store.canvases.find((c) => c.id === activeCanvasId)
          : undefined;
        const header = canvas?.widgets.find(
          (w) =>
            w.type === "profileHeader" &&
            (w.config as any)?.profileId === planProfileId
        );
        if (canvas && header) {
          // Fetch profile and set as input; fire-and-forget with stale-safe guard
          getProfileById(planProfileId)
            .then((res) => {
              try {
                if ((res as any)?.success && (res as any).profile) {
                  this.safeUpdateInputs(header.id, {
                    profile: (res as any).profile,
                  });
                }
              } catch {}
            })
            .catch(() => {});
        }
      }
    } catch {}

    // If grouping is requested, ensure a Run Header widget exists (unless already in the group) and include it in the group
    let runHeaderId: string | undefined;
    if (groupId) {
      try {
        const store = useCanvasStore.getState();
        const activeCanvasId = store.activeCanvasId;
        const canvas = activeCanvasId
          ? store.canvases.find((c) => c.id === activeCanvasId)
          : undefined;
        const group = canvas?.groups?.find((g) => g.id === groupId);
        const groupAlreadyHasHeader = !!group?.widgets.find(
          (w) => w.type === "runHeader"
        );
        const planHasHeader =
          Array.isArray(layout.widgets) &&
          layout.widgets.some((w: any) => String(w.type) === "runHeader");
        if (!groupAlreadyHasHeader && !planHasHeader) {
          // Place run header to the left of the planned widget cluster to avoid overlap
          try {
            const plannedPositions = (layout.widgets || []).map(
              (w: any, idx: number) => {
                const p = w?.position;
                if (p && typeof p.x === "number" && typeof p.y === "number")
                  return p;
                // Approximate default position used during creation
                return { x: 100 + idx * 300, y: 200 };
              }
            );
            const minX =
              plannedPositions.length > 0
                ? Math.min(...plannedPositions.map((p: any) => p.x))
                : 160;
            const minY =
              plannedPositions.length > 0
                ? Math.min(...plannedPositions.map((p: any) => p.y))
                : 120;
            const headerPos = {
              x: Math.max(40, minX - 460),
              y: Math.max(40, minY - 40),
            };
            const header = canvasArchitect.createWidget(
              "runHeader",
              headerPos,
              { title: "Run" }
            );
            if (header.success && header.widgetId) {
              runHeaderId = header.widgetId;
            }
          } catch {
            const header = canvasArchitect.createWidget(
              "runHeader",
              { x: 80, y: 120 },
              { title: "Run" }
            );
            if (header.success && header.widgetId) {
              runHeaderId = header.widgetId;
            }
          }
        }
      } catch {}
    }

    // Optionally connect
    if (Array.isArray(layout.connections)) {
      layout.connections.forEach((c: any) => {
        const sourceWidgetId = createdWidgetIds[c.fromIndex];
        const targetWidgetId = createdWidgetIds[c.toIndex];
        if (!sourceWidgetId || !targetWidgetId) return;
        const fromOutput =
          typeof c.fromOutput === "string" ? c.fromOutput : undefined;
        const toInput = typeof c.toInput === "string" ? c.toInput : undefined;
        const cr = canvasArchitect.connectWidgets(
          sourceWidgetId,
          targetWidgetId,
          fromOutput,
          toInput
        );
        if (!cr.success)
          throw new Error(cr.error || "Failed to connect widgets");
      });
    }

    // Optionally group widgets under a specified groupId
    if (groupId) {
      const store = useCanvasStore.getState();
      const targetCanvasId =
        (toolCall.args?.__targetCanvasId as string | undefined) || store.activeCanvasId;
      if (targetCanvasId) {
        const canvas = store.canvases.find((c) => c.id === targetCanvasId);
        const existing = canvas?.groups?.find((g) => g.id === groupId);
        const groupWidgetIds = runHeaderId
          ? [runHeaderId, ...createdWidgetIds]
          : createdWidgetIds;
        if (!existing) {
          // Create the group if it does not already exist
          store.createGroup(targetCanvasId, groupWidgetIds, "Run", groupId);
        }
        // Initialize/refresh group status and header inputs
        try {
          store.updateGroup(targetCanvasId, groupId, {
            status: "running",
            params: params || {},
          });
          // Prefer the newly created header if any; otherwise try to find an existing run header
          let headerId = runHeaderId;
          if (!headerId && existing) {
            const header = existing.widgets.find((w) => w.type === "runHeader");
            headerId = header?.id;
          }
          if (headerId) {
            store.updateWidgetInputs(targetCanvasId, headerId, {
              status: "running",
              params: params || {},
              trace: existing?.trace || [],
            });
          }
          // Add initial trace entry once per apply
          this.appendRunTrace("Run started", "applyCanvasPlan", groupId);
          // Stay in parent canvas; do not auto-zoom into the run
        } catch {}

        if (this.pendingGroupFocus.has(groupId)) {
          try {
            store.zoomToGroup(targetCanvasId, groupId);
          } catch (err) {
            console.warn(
              "[Orchestrator] Failed to zoom to newly created subcanvas",
              err
            );
          }
          this.pendingGroupFocus.delete(groupId);
        }
      }
    }

    // After creation, animate viewport to show the newly created cluster when placing multiple widgets
    try {
      if (layout.widgets.length >= 3) {
        const bounds = viewportForPlan(layout as any);
        if (bounds) animateViewportTo(bounds, 400);
      }
    } catch {}

    // If placeholders requested, we can flag widgets as loading via config
    if (showPlaceholders) {
      createdWidgetIds.forEach((id) => {
        canvasArchitect.updateWidgetConfig(id, { __loading: true });
      });
    }

    const planHash = args.__planHash || sharedComputePlanHash(args);

    // Fire-and-forget hydration of profile overview widgets (dev-friendly UX)
    try {
      const layoutWidgets: any[] = layout.widgets || [];
      if (typeof planProfileId === "string" && planProfileId.length > 0) {
        setTimeout(async () => {
          try {
            const res = await getProfileById(planProfileId).catch(
              () => null as any
            );
            const profile = res?.profile;
            if (!res?.success || !profile) return;

            const store = useCanvasStore.getState();
            const activeCanvasId = store.activeCanvasId;
            if (!activeCanvasId) return;

            // Map created widget IDs to planned widgets by index
            layoutWidgets.forEach((w, idx) => {
              const wid = createdWidgetIds[idx];
              if (!wid) return;
              // Clear loading flag if set
              canvasArchitect.updateWidgetConfig(wid, { __loading: false });
              try {
                switch (String(w.type)) {
                  case "profileHeader":
                    this.safeUpdateInputs(wid, { profile });
                    break;
                  case "themeAnalysis":
                    if (
                      Array.isArray((profile as any).themes) &&
                      (profile as any).themes.length > 0
                    ) {
                      this.safeUpdateInputs(wid, {
                        themes: (profile as any).themes,
                      });
                    } else {
                      themesApi
                        .discover(planProfileId)
                        .then((d) => {
                          this.safeUpdateInputs(wid, {
                            themes: d.themes || [],
                          });
                        })
                        .catch(() => {});
                    }
                    break;
                  case "newsSentiment": {
                    // Prefer widget-specific ticker when available to avoid reusing the root profile's news
                    const cfgTicker = (w?.config &&
                      (w.config as any).ticker) as string | undefined;
                    const sym = (
                      cfgTicker ||
                      (profile.ticker as string) ||
                      ""
                    ).trim();
                    if (!sym) break;
                    // If this widget targets the profile ticker and we already have cached research news, reuse it; otherwise fetch symbol-specific
                    const isRoot =
                      sym.toUpperCase() ===
                      String(profile.ticker || "").toUpperCase();
                    if (
                      isRoot &&
                      Array.isArray(profile.research?.recentNews) &&
                      profile.research!.recentNews.length > 0
                    ) {
                      this.safeUpdateInputs(wid, {
                        recentNews: profile.research!.recentNews,
                      });
                    } else {
                      newsApi
                        .getStockNews(sym, { profileId: planProfileId })
                        .then((articles) => {
                          this.safeUpdateInputs(wid, {
                            recentNews: articles || [],
                          });
                        })
                        .catch(() => {});
                    }
                    break;
                  }
                  case "relationshipGraph":
                    relationshipsApi
                      .getRelationships(planProfileId)
                      .then((rels) => {
                        this.safeUpdateInputs(wid, { relationships: rels });
                      })
                      .catch(() => {});
                    break;
                  default:
                    break;
                }
              } catch {}
            });
          } catch (e) {
            console.warn("[Orchestrator] Hydration failed:", e);
          }
        }, 0);
      }
    } catch {}

    return {
      success: true,
      widgetIds: createdWidgetIds,
      message: `Applied canvas plan with ${createdWidgetIds.length} widget(s).`,
      planHash,
    };
  }

  // Minimal Mermaid-like DSL parser: nodes (index:type title?) and edges index.output->index.input
  private parseCanvasDSL(dsl: string) {
    // Example:
    // widgets:
    // 0: marketData "AAPL" @100,200
    // 1: marketData "GOOGL" @100,400
    // 2: portfolioComparison "AAPL vs GOOGL" @400,300
    // connections:
    // 0.selectedStock -> 2.stock1
    // 1.selectedStock -> 2.stock2
    const lines = String(dsl || "")
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    const widgets: any[] = [];
    const connections: any[] = [];
    let section: "widgets" | "connections" | undefined;
    for (const line of lines) {
      if (/^widgets:/i.test(line)) {
        section = "widgets";
        continue;
      }
      if (/^connections:/i.test(line)) {
        section = "connections";
        continue;
      }
      if (section === "widgets") {
        const m = line.match(
          /^(\d+):\s*(\w+)\s*("([^"]+)")?\s*(?:@(\d+),(\d+))?$/
        );
        if (m) {
          const idx = parseInt(m[1], 10);
          const type = m[2];
          const title = m[4];
          const x = m[5] ? parseInt(m[5], 10) : undefined;
          const y = m[6] ? parseInt(m[6], 10) : undefined;
          widgets[idx] = {
            type,
            title,
            position: x != null && y != null ? { x, y } : undefined,
          };
        }
      } else if (section === "connections") {
        const m = line.match(/^(\d+)\.(\w+)\s*->\s*(\d+)\.(\w+)$/);
        if (m) {
          connections.push({
            fromIndex: parseInt(m[1], 10),
            fromOutput: m[2],
            toIndex: parseInt(m[3], 10),
            toInput: m[4],
          });
        }
      }
    }
    return { widgets: widgets.filter(Boolean), connections };
  }

  // Plan hashing unified via @akem/shared (computePlanHash)

  private handleConnectWidgets(toolCall: ToolCall) {
    // Prevent modifications on read-only canvases
    try {
      const store = useCanvasStore.getState();
      const targetCanvasId =
        (toolCall.args?.__targetCanvasId as string | undefined) || store.activeCanvasId;
      if (targetCanvasId) {
        const c = store.canvases.find((cc) => cc.id === targetCanvasId);
        if (c && c.kind && c.kind !== 'custom') {
          return { success: false, error: 'Canvas is read-only' };
        }
      }
    } catch {}
    // Prevent modifications on read-only canvases
    try {
      const store = useCanvasStore.getState();
      const active = store.getActiveCanvas();
      if (active && active.kind && active.kind !== 'custom') {
        return { success: false, error: 'Canvas is read-only' };
      }
    } catch {}
    const {
      sourceWidgetId,
      targetWidgetId,
      outputId,
      inputId,
      fromOutput,
      toInput,
    } = toolCall.args || {};
    const out = outputId ?? fromOutput;
    const inp = inputId ?? toInput;
    const result = canvasArchitect.connectWidgets(
      sourceWidgetId,
      targetWidgetId,
      out,
      inp
    );
    if (result.success) {
      try {
        const gid =
          toolCall.args?.groupId ??
          (toolCall.args?.sessionId
            ? this.runBySession.get(toolCall.args.sessionId)
            : undefined);
        this.appendRunTrace(
          `Connected ${sourceWidgetId}.${out || ""} → ${targetWidgetId}.${
            inp || ""
          }`,
          "connectWidgets",
          gid
        );
      } catch {}
      return {
        success: true,
        connectionId: result.connectionId,
        message: `Connected widgets.`,
      };
    }
    throw new Error(result.error || "Failed to connect widgets");
  }

  private handleUpdateWidget(toolCall: ToolCall) {
    const { widgetId, config } = toolCall.args;
    const success = canvasArchitect.updateWidgetConfig(widgetId, config);
    if (success) {
      try {
        const gid =
          toolCall.args?.groupId ??
          (toolCall.args?.sessionId
            ? this.runBySession.get(toolCall.args.sessionId)
            : undefined);
        this.appendRunTrace(
          `Updated widget ${widgetId} config`,
          "updateWidget",
          gid
        );
      } catch {}
      return { success: true, widgetId: widgetId, message: `Updated widget.` };
    }
    throw new Error(`Failed to update widget ${widgetId}`);
  }

  private handleUpdateWidgetInputs(toolCall: ToolCall) {
    // Prevent modifications on read-only canvases
    try {
      const store = useCanvasStore.getState();
      const active = store.getActiveCanvas();
      if (active && active.kind && active.kind !== 'custom') {
        return { success: false, error: 'Canvas is read-only' };
      }
    } catch {}
    const { target, inputs } = toolCall.args || {};
    const store = useCanvasStore.getState();
    const activeCanvasId =
      (toolCall.args?.__targetCanvasId as string | undefined) || store.activeCanvasId;
    if (!activeCanvasId) throw new Error("No active canvas");

    let wid: string | undefined = target?.widgetId;
    if (!wid && target?.type) {
      let widgets;
      if (target.groupId) {
        const canvas = store.canvases.find((c) => c.id === activeCanvasId);
        const group = canvas?.groups?.find((g) => g.id === target.groupId);
        widgets = group?.widgets || [];
      } else {
        widgets = canvasArchitect.getAllWidgets();
      }
      const match = widgets.find((w) => w.type === target.type);
      wid = match?.id;
    }
    if (!wid) throw new Error("Target widget not found");
    try {
      store.updateWidgetInputs(activeCanvasId, wid, inputs || {});
      // Trace: prefer groupId from target or from tool args; avoid global state
      try {
        const t = target || {};
        const gid =
          (t.groupId as string | undefined) ||
          toolCall.args?.groupId ||
          (toolCall.args?.sessionId
            ? this.runBySession.get(toolCall.args.sessionId)
            : undefined);
        const label = t.type
          ? `Updated ${t.type} inputs`
          : `Updated inputs for ${wid}`;
        this.appendRunTrace(label, "updateWidgetInputs", gid);
      } catch {}
      return { success: true, widgetId: wid };
    } catch (e) {
      throw new Error("Failed to update widget inputs");
    }
  }

  private handleDeleteWidget(toolCall: ToolCall) {
    // Prevent modifications on read-only canvases
    try {
      const store = useCanvasStore.getState();
      const active = store.getActiveCanvas();
      if (active && active.kind && active.kind !== 'custom') {
        return { success: false, error: 'Canvas is read-only' };
      }
    } catch {}
    const { widgetId } = toolCall.args;
    // This needs to be implemented in the actual canvasArchitect class
    console.log(`[Orchestrator] Deleting widget ${widgetId}`);
    try {
      const gid =
        toolCall.args?.groupId ??
        (toolCall.args?.sessionId
          ? this.runBySession.get(toolCall.args.sessionId)
          : undefined);
      this.appendRunTrace(`Deleted widget ${widgetId}`, "deleteWidget", gid);
    } catch {}
    return { success: true, widgetId: widgetId, message: `Deleted widget.` };
  }

  private isBackendTool(toolName: string): boolean {
    return isBackendToolStatic(toolName);
  }

  private handleCreateDiscoveryNode(toolCall: ToolCall) {
    // Debug log to see what we're receiving
    console.log(
      "[Orchestrator] createDiscoveryNode received toolCall:",
      toolCall
    );

    // Validate args exist and provide defaults
    if (!toolCall.args) {
      console.error(
        "[Orchestrator] createDiscoveryNode called without arguments, toolCall:",
        toolCall
      );
      return {
        success: false,
        error:
          "Missing arguments for discovery node creation. Need at least a title.",
      };
    }

    // Destructure with defaults to handle missing fields
    const {
      title = "Explore Further",
      description = "Discover new insights and opportunities",
      prompt = "What would you like to explore?",
      position,
      anchorWidgetId,
      pinned = false,
    } = toolCall.args;

    // Get the target/active canvas from the store
    const store = useCanvasStore.getState();
    const activeCanvasId =
      (toolCall.args?.__targetCanvasId as string | undefined) || store.activeCanvasId;

    if (!activeCanvasId) {
      return {
        success: false,
        error: "No active canvas found",
      };
    }

    // Prevent discovery nodes on dashboard canvases (market/profile)
    try {
      const canvas = store.canvases.find((c) => c.id === activeCanvasId);
      if (canvas && canvas.kind && canvas.kind !== 'custom') {
        return { success: false, error: 'Discovery nodes are disabled on dashboards' };
      }
    } catch {}

    try {
      // Create the discovery node (store will dedupe by title+anchor)
      const nodeId = store.addDiscoveryNode(activeCanvasId, {
        title,
        description,
        prompt,
        position,
        anchorWidgetId,
        pinned,
      });

      console.log(
        `[Orchestrator] Created discovery node: ${title} with ID ${nodeId}`
      );
      try {
        const gid =
          toolCall.args?.groupId ??
          (toolCall.args?.sessionId
            ? this.runBySession.get(toolCall.args.sessionId)
            : undefined);
        this.appendRunTrace(`Discovery: ${title}`, "createDiscoveryNode", gid);
      } catch {}
      return {
        success: true,
        nodeId,
        title,
        message: `Created discovery node: ${title}`,
      };
    } catch (error) {
      console.error("[Orchestrator] Failed to create discovery node:", error);
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to create discovery node",
      };
    }
  }

  private inferProfileIdFromCanvas(
    canvas?: StoreCanvasState
  ): string | undefined {
    if (!canvas) return undefined;

    if (typeof canvas.key === "string") {
      const match = canvas.key.match(/^profile:(.+)$/);
      if (match && match[1]) {
        return match[1];
      }
    }

    const fromWidgets = (canvas.widgets || [])
      .map((w) => (w.config as any)?.profileId)
      .find((pid) => typeof pid === "string" && pid.length > 0);
    if (fromWidgets) {
      return fromWidgets;
    }

    for (const group of canvas.groups || []) {
      const withinGroup = (group.widgets || [])
        .map((w) => (w.config as any)?.profileId)
        .find((pid) => typeof pid === "string" && pid.length > 0);
      if (withinGroup) return withinGroup;
    }

    return undefined;
  }

  // Safely update widget inputs if the widget still exists on the active canvas
  private async safeUpdateInputs(widgetId: string, inputs: any) {
    try {
      const store = useCanvasStore.getState();
      const active = store.activeCanvasId;
      if (!active) return;
      const stillExists = !!store.getWidget(active, widgetId);
      if (stillExists) store.updateWidgetInputs(active, widgetId, inputs);
    } catch {}
  }

  private getCurrentCanvasState(): CanvasState {
    const widgets = canvasArchitect.getAllWidgets();
    const connections = canvasArchitect.getAllConnections();
    const store = useCanvasStore.getState();
    const activeId = store.activeCanvasId;
    const activeCanvas = activeId
      ? store.canvases.find((c) => c.id === activeId)
      : undefined;

    return {
      widgets: widgets.map((w) => ({
        id: w.id,
        type: w.type,
        title: w.config?.title,
        position: w.position,
        outputs: w.outputs,
      })),
      connections: connections.map((c) => ({
        id: c.id,
        source: c.source,
        target: c.target,
      })),
      discoveryNodes:
        activeCanvas?.discoveryNodes?.map((n) => ({
          id: n.id,
          title: n.title,
          description: n.description,
          prompt: n.prompt,
          position: n.position,
          anchorWidgetId: n.anchorWidgetId,
          pinned: n.pinned,
        })) || [],
    };
  }

  // Append a compact trace entry to the current Run (by groupId or most recent)
  private appendRunTrace(text: string, toolName?: string, groupId?: string) {
    try {
      const store = useCanvasStore.getState();
      const activeCanvasId = store.activeCanvasId;
      if (!activeCanvasId) return;
      const canvas = store.canvases.find((c) => c.id === activeCanvasId);
      if (!canvas) return;
      const group = groupId
        ? canvas.groups?.find((g) => g.id === groupId)
        : (canvas.groups || []).slice(-1)[0];
      if (!group) return;
      const prevTrace: Array<{ t: number; text: string; tool?: string }> =
        (group as any).trace || [];
      const entry = { t: Date.now(), text, tool: toolName };
      const nextTrace = [...prevTrace, entry].slice(-100); // cap
      store.updateGroup(activeCanvasId, group.id, { trace: nextTrace });
      const header = group.widgets.find((w) => w.type === "runHeader");
      if (header) {
        store.updateWidgetInputs(activeCanvasId, header.id, {
          trace: nextTrace,
        });
      }
    } catch {}
  }

  private async callAgentAPI(
    messages: ChatMessage[],
    canvasState?: CanvasState,
    config: Partial<AgentConfig> = {}
  ) {
    const toolStore = useToolExecutionStore.getState();

    const envelope = this.buildCanvasContextEnvelope();
    // If we have a run sessionId, poll backend progress in parallel so user sees live steps
    const sessionIdForProgress = this.currentRunGroupId;
    let cancelProgress = false;
    let lastProgressTs = 0;
    let hadLiveProgress = false;
    const progressPoller = async () => {
      if (!sessionIdForProgress) return;
      try {
        while (!cancelProgress) {
          const pr = await fetch(
            `${getApiEndpoint()}/agent/progress?sessionId=${encodeURIComponent(
              sessionIdForProgress
            )}`
          );
          if (pr.ok) {
            const body = await pr.json().catch(() => ({}));
            const entries: Array<{
              t: number;
              text?: string;
              backend?: string[];
              frontend?: string[];
            }> = Array.isArray(body.progress) ? body.progress : [];
            const newEntries = entries.filter(
              (e) => typeof e.t === "number" && e.t > lastProgressTs
            );
            if (newEntries.length > 0) {
              hadLiveProgress = true;
              lastProgressTs = Math.max(
                lastProgressTs,
                ...newEntries.map((e) => e.t)
              );
              newEntries.forEach((e) => {
                const label =
                  e.text ||
                  (Array.isArray(e.backend) && e.backend.length
                    ? `Backend: ${e.backend.join(", ")}`
                    : undefined) ||
                  (Array.isArray(e.frontend) && e.frontend.length
                    ? `Frontend: ${e.frontend.join(", ")}`
                    : "Agent step");
                try {
                  this.appendRunTrace(
                    label || "Agent step",
                    (e.backend && e.backend[0]) || undefined,
                    sessionIdForProgress
                  );
                } catch {}
              });
            }
          }
          // Poll interval with mild backoff if nothing new
          await new Promise((r) => setTimeout(r, 800));
        }
      } catch {
        // ignore transient errors
      }
    };
    // Fire-and-forget; will be cancelled when main request settles
    if (sessionIdForProgress) progressPoller();

    const response = await fetch(`${getApiEndpoint()}/agent`, {
      // Main agent endpoint
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        messages,
        // Always send current canvas state including discovery nodes so backend has up-to-date awareness
        canvasState: canvasState ?? this.getCurrentCanvasState(),
        // Send compact, delta-driven context envelope for agent reasoning
        canvasContext: envelope,
        temperature: config.temperature,
        // Provide run-scoped sessionId when available so backend can maintain per-run context
        discoveryState: this.currentRunGroupId
          ? {
              sessionId: this.currentRunGroupId,
              currentLevel: "foundation",
              exploredConnections: [],
              unlockedCapabilities: [],
              lastInteractionTime: new Date().toISOString(),
              userEngagementScore: 0,
            }
          : undefined,
      }),
    });
    // Stop progress polling once the main request finishes
    cancelProgress = true;

    // Handle async 202: poll progress until completion
    if (response.status === 202) {
      const enqueued = await response.json().catch(() => ({}));
      const sessionId = enqueued.sessionId;
      if (!sessionId) {
        throw new Error("Async enqueue did not return sessionId");
      }
      const start = Date.now();
      let attempt = 0;
      const maxMs = 10 * 60 * 1000; // 10 minutes max wait client-side
      while (Date.now() - start < maxMs) {
        attempt++;
        const pr = await fetch(
          `${getApiEndpoint()}/agent/progress?sessionId=${encodeURIComponent(
            sessionId
          )}`
        );
        if (pr.ok) {
          const statusBody = await pr.json().catch(() => ({}));
          // Surface live step progress if provided
          try {
            const entries: Array<{
              t: number;
              text?: string;
              backend?: string[];
              frontend?: string[];
            }> = Array.isArray(statusBody.progress) ? statusBody.progress : [];
            const newEntries = entries.filter(
              (e) => typeof e.t === "number" && e.t > lastProgressTs
            );
            if (newEntries.length > 0) {
              hadLiveProgress = true;
              lastProgressTs = Math.max(
                lastProgressTs,
                ...newEntries.map((e) => e.t)
              );
              newEntries.forEach((e) => {
                const label =
                  e.text ||
                  (Array.isArray(e.backend) && e.backend.length
                    ? `Backend: ${e.backend.join(", ")}`
                    : undefined) ||
                  (Array.isArray(e.frontend) && e.frontend.length
                    ? `Frontend: ${e.frontend.join(", ")}`
                    : "Agent step");
                this.appendRunTrace(
                  label || "Agent step",
                  (e.backend && e.backend[0]) || undefined,
                  sessionId
                );
              });
            }
          } catch {}
          if (statusBody?.status === "completed" && statusBody?.result) {
            return statusBody.result;
          }
          if (statusBody?.status === "failed") {
            throw new Error(statusBody?.error || "Agent job failed");
          }
        }
        // Backoff: 500ms -> 2s capped
        const sleepMs = Math.min(2000, 500 * Math.pow(1.4, attempt));
        await new Promise((r) => setTimeout(r, sleepMs));
      }
      throw new Error("Agent job timed out while waiting for completion");
    }

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ error: "Unknown error" }));
      throw new Error(
        error.error || `API request failed with status ${response.status}`
      );
    }

    const result = await response.json();

    // Surface backend step traces to the UI (plan/reflect/validate/etc.)
    try {
      if (!hadLiveProgress && Array.isArray(result.debugSteps)) {
        const syntheticFrontEndCalls: ToolCall[] = [];
        result.debugSteps.forEach((step: any, idx: number) => {
          const calls: Array<{ toolName: string; args: any }> =
            step.toolCalls || [];
          // Derive run groupId, if present, from this step's tool calls
          const fromApply = calls.find(
            (c) =>
              c.toolName === "applyCanvasPlan" &&
              c.args &&
              (c.args.groupId || (c.args.target && c.args.target.groupId))
          );
          const fromUpdate = calls.find(
            (c) =>
              c.toolName === "updateWidgetInputs" &&
              c.args &&
              (c.args.target?.groupId || c.args.groupId)
          );
          const derivedGroupId =
            fromApply?.args?.groupId ||
            fromApply?.args?.target?.groupId ||
            fromUpdate?.args?.target?.groupId ||
            fromUpdate?.args?.groupId;
          // Append trace entry to run, if we can identify the run
          try {
            if (step?.text)
              this.appendRunTrace(
                step.text,
                calls?.[0]?.toolName,
                derivedGroupId
              );
          } catch {}
          calls.forEach((c) => {
            const actionId = agentFeedbackService.trackAction(
              `debug-${idx}-${c.toolName}-${Date.now()}`,
              c.toolName,
              c.args || {},
              {
                description: step.text || `Agent step: ${c.toolName}`,
              }
            );
            // Immediately mark as completed for backend-only steps so they appear in the timeline
            agentFeedbackService.updateActionStatus(actionId, "completed", {
              message: step.text,
            });

            // Also reflect in ToolExecutionIndicator (tool store) so backend steps show up there too
            try {
              const toolId = toolStore.startToolExecution(
                c.toolName,
                c.args || {},
                "backend"
              );
              toolStore.completeToolExecution(toolId, { message: step.text });
            } catch {}

            // If the backend suggests a frontend tool (e.g., updateWidgetInputs), execute it
            if (
              ["updateWidgetInputs"].includes(c.toolName) &&
              c.args &&
              typeof c.args === "object"
            ) {
              syntheticFrontEndCalls.push({
                id: `debug-call-${idx}-${Date.now()}`,
                toolName: c.toolName,
                args: c.args,
              });
            }
          });
        });

        if (syntheticFrontEndCalls.length > 0) {
          await this.delegateToolCalls(syntheticFrontEndCalls);
        }
      }
    } catch (e) {
      console.warn("[Orchestrator] Failed to surface/execute debug steps:", e);
    }

    // Extract and track agent thinking from the response
    if (result.message && result.message.content) {
      // Look for thinking patterns in the agent's response
      const content = result.message.content;
      const thinkingPatterns = [
        /I'll (?:need to |start by |begin with |first )(.+?)(?:\.|$)/i,
        /Let me (?:think about |analyze |consider |examine )(.+?)(?:\.|$)/i,
        /(?:Analyzing|Planning|Considering|Evaluating) (.+?)(?:\.|$)/i,
        /To (?:accomplish|achieve|complete) this, (.+?)(?:\.|$)/i,
      ];

      for (const pattern of thinkingPatterns) {
        const match = content.match(pattern);
        if (match) {
          toolStore.setAgentThinking(match[1].trim());
          break;
        }
      }

      // Clear thinking when agent is done thinking (has tool calls or final response)
      if (result.message.toolCalls && result.message.toolCalls.length > 0) {
        // Agent has decided on actions, clear thinking after a delay
        setTimeout(() => {
          toolStore.setAgentThinking(undefined);
        }, 1500);
      } else if (!content.includes("?")) {
        // Final response without question, clear thinking
        setTimeout(() => {
          toolStore.setAgentThinking(undefined);
        }, 2000);
      }
    }

    return result;
  }

  // Build a compact, delta-driven context envelope from the active canvas (structure + intent only)
  private buildCanvasContextEnvelope(): CanvasContextEnvelope {
    try {
      const store = useCanvasStore.getState();
      const activeId = store.activeCanvasId;
      const canvas = activeId
        ? store.canvases.find((c) => c.id === activeId)
        : undefined;
      if (!canvas) return { widgets: [] };

      const widgets = canvas.widgets.map((w) => {
        const cfg: any = w.config || {};
        const inputsById: Record<string, any> = Object.fromEntries(
          (w.inputs || []).map((i) => [i.id, i.value])
        );
        const dataRefs: Array<{ kind: string; key: string }> = [];
        const counts: Record<string, number> = {};
        const sample: Array<{ id?: string; ts?: number; title?: string }> = [];
        let digest: string | undefined;

        // Heuristics per widget type
        switch (String(w.type)) {
          case "newsSentiment": {
            const symbol: string | undefined =
              (cfg.ticker as string) || (inputsById.ticker as string);
            const sinceMs = Date.now() - 24 * 60 * 60 * 1000; // 24h default
            if (symbol)
              dataRefs.push({ kind: "news", key: `news:${symbol}:${sinceMs}` });
            const items = (inputsById.recentNews as any[]) || [];
            counts.articles = Array.isArray(items) ? items.length : 0;
            const ids = (items || []).map((n) =>
              String(
                (n as any).id ||
                  `${(n as any).source || ""}:${(n as any).title || ""}`
              )
            );
            digest = ids.length > 0 ? sharedComputePlanHash(ids) : undefined;
            (items || []).slice(0, 3).forEach((n) => {
              sample.push({
                id: String((n as any).id || ""),
                ts: (n as any).publishedAt
                  ? new Date((n as any).publishedAt).getTime()
                  : undefined,
                title: (n as any).title,
              });
            });
            break;
          }
          case "priceChart": {
            const symbol: string | undefined =
              (inputsById.ticker as string) || (cfg.ticker as string);
            const range: string | undefined =
              (cfg.range as string) || undefined; // UI owns actual range state
            if (symbol) {
              dataRefs.push({ kind: "quote", key: `quote:${symbol}` });
              dataRefs.push({
                kind: "intraday",
                key: `intraday:${symbol}:range=${range || "3M"}`,
              });
              digest = sharedComputePlanHash([symbol, range || "3M"]);
            }
            break;
          }
          case "profileHeader": {
            const profileId = (cfg.profileId as string) || undefined;
            if (profileId)
              dataRefs.push({ kind: "profile", key: `profile:${profileId}` });
            break;
          }
          default: {
            // Generic: expose key config hints for common keys without data
            break;
          }
        }

        // Select key config hints (structure, not data)
        const configKeys: Record<string, any> = {};
        ["ticker", "profileId", "symbols", "range", "sinceMs"].forEach((k) => {
          if (cfg[k] !== undefined) configKeys[k] = cfg[k];
        });

        return {
          id: w.id,
          type: w.type,
          title: (cfg?.title as string) || undefined,
          configKeys,
          lastUpdated: w.state?.lastUpdated
            ? new Date(w.state.lastUpdated).getTime?.() || undefined
            : undefined,
          dataRefs,
          digest,
          counts: Object.keys(counts).length > 0 ? counts : undefined,
          sample,
          ui: undefined,
        };
      });

      // Compute a simple canvasDigest over widget type+digest
      const digestBase = widgets.map((w) => `${w.type}:${w.digest || "-"}`);
      const canvasDigest =
        digestBase.length > 0 ? sharedComputePlanHash(digestBase) : undefined;

      return {
        canvasDigest,
        focus: { activeWidgetIds: [], viewport: canvas.viewport },
        widgets,
      };
    } catch {
      return { widgets: [] };
    }
  }

  private async callCanvasArchitectAPI(
    messages: ChatMessage[],
    instruction: string | null,
    canvasState: CanvasState
  ) {
    const toolStore = useToolExecutionStore.getState();

    const body: any = {
      canvasState,
      agentType: "canvas-architect", // Indicate which agent to use
    };
    if (messages.length > 0) {
      body.messages = messages;
    } else {
      body.instruction = instruction;
    }

    // Note: Using /agent endpoint - backend will route based on agentType
    const response = await fetch(`${getApiEndpoint()}/agent`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ error: "Unknown error" }));
      throw new Error(
        error.error ||
          `Canvas Architect API request failed with status ${response.status}`
      );
    }

    const result = await response.json();

    // Track Canvas Architect thinking
    if (result.message && result.message.content) {
      const content = result.message.content;
      // Canvas Architect specific thinking patterns
      const architectPatterns = [
        /I'll (?:create|add|connect|update|modify) (.+?)(?:\.|$)/i,
        /(?:Creating|Adding|Connecting|Building) (.+?)(?:\.|$)/i,
        /To (?:visualize|display|show) this, (.+?)(?:\.|$)/i,
        /Setting up (.+?)(?:\.|$)/i,
      ];

      for (const pattern of architectPatterns) {
        const match = content.match(pattern);
        if (match) {
          toolStore.setAgentThinking(`Canvas: ${match[1].trim()}`);
          break;
        }
      }

      // Clear after action
      if (result.message.toolCalls && result.message.toolCalls.length > 0) {
        setTimeout(() => {
          const currentThinking =
            useToolExecutionStore.getState().agentThinking;
          if (currentThinking?.startsWith("Canvas:")) {
            useToolExecutionStore.getState().setAgentThinking(undefined);
          }
        }, 1500);
      }
    }

    return result;
  }

  // Caching and utility methods remain the same...
  private generateCacheKey(
    task: string | ChatMessage[],
    config: AgentConfig
  ): string {
    const taskKey = typeof task === "string" ? task : JSON.stringify(task);
    return taskKey;
  }

  clearCache(): void {
    this.cache.clear();
  }
}

export const orchestratorAgent = new OrchestratorAgent();
